import type {
  ComparableCoolingObservationValue,
  ComparableDeliveryEventValue,
  ComparableEvidenceAuthority,
  ComparablePowerObservationValue,
  ComparableProjectRecord,
  ComparableWaterObservationValue,
  EconomicSourceRef,
} from '../../types/economicContribution';

export const COMPARABLE_RECORD_POLICY_ID = 'comparable-public-record-policy-v2' as const;

interface MaterialObservation {
  observationId?: string;
  value: unknown;
  evidenceAuthority?: ComparableEvidenceAuthority;
  evidenceBasis: string;
  sources: readonly EconomicSourceRef[];
  phaseBasis: string;
  structuredKind?: StructuredObservationValue['kind'];
}

type StructuredObservationValue =
  | ComparableWaterObservationValue
  | ComparableCoolingObservationValue
  | ComparablePowerObservationValue
  | ComparableDeliveryEventValue;

const WATER_UNITS = new Set([
  'gal/day', 'million-gal/day', 'acre-ft/year', 'liters/day',
  'gallons', 'million-gallons', 'acre-ft', 'liters',
]);

function isStructuredObservationValue(value: unknown): value is StructuredObservationValue {
  if (value == null || typeof value !== 'object' || !('kind' in value)) return false;
  return ['water', 'cooling', 'power', 'delivery-event'].includes(String(value.kind));
}

function materialObservations(record: ComparableProjectRecord): MaterialObservation[] {
  const possible = [
    record.mw, record.rackCount, record.buildingAreaSqFt, record.capitalInvestmentUsd,
    record.capitalInvestmentUsdRange, record.constructionPeakWorkers,
    record.constructionPeakWorkersRange, record.constructionJobYears, record.permanentJobs,
    record.permanentJobsRange,
    record.annualPayrollUsd, record.averageWageUsd, record.assessedValueUsd,
    record.propertyTaxPaidUsd, record.pilotPaymentUsd, record.water,
  ];
  const result: MaterialObservation[] = [];
  for (const observation of possible) {
    if (observation != null) result.push(observation as MaterialObservation);
  }
  result.push(
    ...(record.waterTimeline ?? []).map((observation) => ({ ...observation, structuredKind: 'water' as const })),
    ...(record.coolingObservations ?? []).map((observation) => ({ ...observation, structuredKind: 'cooling' as const })),
    ...(record.powerObservations ?? []).map((observation) => ({ ...observation, structuredKind: 'power' as const })),
    ...(record.deliveryEvents ?? []).map((observation) => ({ ...observation, structuredKind: 'delivery-event' as const })),
  );
  return result;
}

function validDate(value: string | undefined): boolean {
  return Boolean(value && Number.isFinite(Date.parse(value)));
}

function validateWater(value: ComparableWaterObservationValue): string[] {
  const errors: string[] = [];
  if (!Number.isFinite(value.amount) || value.amount < 0) {
    errors.push('Water observations require a finite non-negative amount.');
  }
  if (!WATER_UNITS.has(value.units)) errors.push('Water observations require a supported native unit.');
  if (value.normalizedGallonsPerDay != null
    && (!Number.isFinite(value.normalizedGallonsPerDay) || value.normalizedGallonsPerDay < 0)) {
    errors.push('Normalized water use must be finite and non-negative.');
  }
  if ((value.stage === 'application-request' || value.stage === 'permit-limit')
    && !value.permitOrApplicationId?.trim()) {
    errors.push('Application requests and permit limits require a permit or application identifier.');
  }
  if ((value.periodStart == null) !== (value.periodEnd == null)) {
    errors.push('Water reporting periods require both a start and end date.');
  }
  if (value.periodStart != null && (!validDate(value.periodStart) || !validDate(value.periodEnd))) {
    errors.push('Water reporting periods require valid dates.');
  }
  if (validDate(value.periodStart) && validDate(value.periodEnd)
    && Date.parse(value.periodEnd!) < Date.parse(value.periodStart!)) {
    errors.push('Water reporting period end date must not precede its start date.');
  }
  if (value.stage === 'reported-actual' && (!validDate(value.periodStart) || !validDate(value.periodEnd))) {
    errors.push('Reported-actual water observations require a reporting period.');
  }
  return errors;
}

function validateCooling(value: ComparableCoolingObservationValue): string[] {
  const axes = [value.rackCoolingStrategy, value.heatRejection, value.coolingGeneration]
    .filter((axis) => axis != null && axis !== 'unknown');
  const native = value.nativeDescription?.trim() ?? '';
  if (native.length > 280) return ['Native cooling descriptions must not exceed 280 characters.'];
  return axes.length > 0 || native.length > 0
    ? []
    : ['Cooling observations require a canonical axis or preserved native wording.'];
}

function validatePower(value: ComparablePowerObservationValue): string[] {
  const errors: string[] = [];
  if (value.technologies.length === 0 || value.technologies.every((technology) => technology === 'unknown')) {
    errors.push('Power observations require at least one known technology.');
  }
  if ((value.capacityMw == null) !== (value.capacityBasis == null)) {
    errors.push('Power capacity and its MW basis must be supplied together.');
  }
  if (value.capacityMw != null && (!Number.isFinite(value.capacityMw) || value.capacityMw <= 0)) {
    errors.push('Power capacity must be finite and positive.');
  }
  if (value.role === 'backup-generation' && value.capacityBasis != null
    && value.capacityBasis !== 'backup-generation') {
    errors.push('Backup generation capacity must use the backup-generation MW basis.');
  }
  if (value.role !== 'backup-generation' && value.capacityBasis === 'backup-generation') {
    errors.push('Backup-generation MW cannot be used for load service, primary generation, or contracts.');
  }
  return errors;
}

function validateDeliveryEvent(value: ComparableDeliveryEventValue): string[] {
  const errors: string[] = [];
  if (!validDate(value.occurredOn)) errors.push('Delivery events require a valid occurrence date.');
  const summary = value.summary.trim();
  if (summary.length < 10 || summary.length > 500) {
    errors.push('Delivery-event summaries must contain 10–500 characters.');
  }
  if (value.scheduleImpactMonths != null
    && (!Number.isFinite(value.scheduleImpactMonths) || value.scheduleImpactMonths < 0)) {
    errors.push('Schedule impacts must be finite and non-negative.');
  }
  if (value.capacityImpactMw != null
    && (!Number.isFinite(value.capacityImpactMw) || value.capacityImpactMw < 0)) {
    errors.push('Capacity impacts must be finite and non-negative.');
  }
  return errors;
}

function validateStructuredObservation(observation: MaterialObservation): string[] {
  const errors: string[] = [];
  if (!isStructuredObservationValue(observation.value)) {
    return [`Structured ${observation.structuredKind ?? 'unknown'} observations require a recognized discriminated value.`];
  }
  if (observation.structuredKind !== observation.value.kind) {
    return [`Structured ${observation.structuredKind ?? 'unknown'} observation has the wrong value kind.`];
  }
  if (!observation.observationId?.trim()) {
    errors.push('Structured observations require a stable observationId.');
  }
  if (!observation.evidenceAuthority) {
    errors.push('Structured observations require an evidence authority separate from lifecycle.');
  } else if (observation.evidenceAuthority === 'discovery-only') {
    errors.push('Discovery-only evidence cannot be promoted.');
  }
  if (observation.evidenceBasis === 'secondary-discovery') {
    errors.push('Secondary-discovery claims cannot be promoted as structured observations.');
  }
  switch (observation.value.kind) {
    case 'water':
      errors.push(...validateWater(observation.value));
      break;
    case 'cooling':
      errors.push(...validateCooling(observation.value));
      break;
    case 'power':
      errors.push(...validatePower(observation.value));
      break;
    case 'delivery-event':
      errors.push(...validateDeliveryEvent(observation.value));
      break;
  }
  return errors;
}

function validateLegacyValue(value: unknown): string[] {
  if (typeof value === 'number' && (!Number.isFinite(value) || value < 0)) {
    return ['Numeric observations must be finite and non-negative.'];
  }
  if (typeof value === 'object' && value != null && 'low' in value && 'high' in value) {
    const range = value as { low: unknown; high: unknown };
    if (typeof range.low !== 'number' || typeof range.high !== 'number'
      || !Number.isFinite(range.low) || !Number.isFinite(range.high)
      || range.low < 0 || range.high < range.low) {
      return ['Range observations require finite non-negative low/high values in ascending order.'];
    }
  }
  return [];
}

export function validateComparableForApproval(record: ComparableProjectRecord): string[] {
  const errors: string[] = [];
  if (!record.projectId.trim()) errors.push('projectId is required.');
  if (!record.facilityName.trim()) errors.push('facilityName is required.');
  if (!/^\d{2}$/.test(record.location.stateFips) || !/^\d{5}$/.test(record.location.countyFips)) {
    errors.push('Valid state and county FIPS are required.');
  }
  const observations = materialObservations(record);
  const structuredIds = new Set<string>();
  if (!record.proposedBy?.trim() || !record.proposedAt || !Number.isFinite(Date.parse(record.proposedAt))) {
    errors.push('proposedBy and a valid proposedAt date are required for the audit trail.');
  }
  if (observations.length === 0) errors.push('At least one material observation is required.');
  for (const observation of observations) {
    if (!observation.phaseBasis.trim()) errors.push('Every material observation requires a phase basis.');
    if (observation.sources.length === 0) errors.push('Every material observation requires a primary source.');
    for (const source of observation.sources) {
      if (!source.id.trim() || !source.title.trim() || !source.publisher.trim()) {
        errors.push('Every source requires an id, title, and publisher.');
      }
      if (!/^https:\/\//.test(source.url)) errors.push('Every source requires an HTTPS URL.');
      if (!Number.isFinite(Date.parse(source.accessedAt))) errors.push('Every source requires a valid accessedAt date.');
      if (!source.excerpt.trim() || source.excerpt.length > 280) {
        errors.push('Every source requires a stored excerpt of 1–280 characters.');
      }
      if (!source.locator.trim()) errors.push('Every source requires a page, table, section, or record locator.');
    }
    if (observation.structuredKind) {
      errors.push(...validateStructuredObservation(observation));
      const id = observation.observationId?.trim();
      if (id) {
        if (structuredIds.has(id)) errors.push(`Structured observationId ${id} is duplicated.`);
        structuredIds.add(id);
      }
    } else {
      errors.push(...validateLegacyValue(observation.value));
    }
  }
  if (record.mw && (!(record.mw.value.value > 0) || !Number.isFinite(record.mw.value.value))) {
    errors.push('MW observations require a finite positive value.');
  }
  const namedReview = Boolean(record.reviewedBy?.trim() && record.reviewedAt && Number.isFinite(Date.parse(record.reviewedAt)));
  const policyValidation = record.validationPolicyId === COMPARABLE_RECORD_POLICY_ID
    && Boolean(record.validatedAt && Number.isFinite(Date.parse(record.validatedAt)))
    && Boolean(record.sourceRecordHash && /^[a-f0-9]{64}$/.test(record.sourceRecordHash));
  if (record.reviewStatus === 'approved' && !namedReview && !policyValidation) {
    errors.push(`Approved records require named review or ${COMPARABLE_RECORD_POLICY_ID}.`);
  }
  return [...new Set(errors)];
}

/** Policy path for exact, structured public records; ambiguous documents remain proposed. */
export function policyApproveComparableProject(
  proposed: ComparableProjectRecord,
  validation: { validatedAt: string; sourceRecordHash: string },
): ComparableProjectRecord {
  const approved: ComparableProjectRecord = {
    ...proposed,
    reviewStatus: 'approved',
    validationPolicyId: COMPARABLE_RECORD_POLICY_ID,
    validatedAt: validation.validatedAt,
    sourceRecordHash: validation.sourceRecordHash,
  };
  const errors = validateComparableForApproval(approved);
  if (errors.length) throw new Error(`Comparable policy validation failed: ${errors.join(' ')}`);
  return approved;
}

export function approveComparableProject(
  proposed: ComparableProjectRecord,
  review: { reviewedBy: string; reviewedAt: string },
): ComparableProjectRecord {
  if (!review.reviewedBy.trim() || !review.reviewedAt.trim()) throw new Error('A named reviewer and review date are required.');
  const approved: ComparableProjectRecord = {
    ...proposed,
    reviewStatus: 'approved',
    reviewedBy: review.reviewedBy.trim(),
    reviewedAt: review.reviewedAt,
  };
  const errors = validateComparableForApproval(approved);
  if (errors.length) throw new Error(`Comparable approval failed: ${errors.join(' ')}`);
  return approved;
}
